import type { FormattedError } from '@shared/utils/RequestUtils';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { cn } from '@shared/utils/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfitabilityByProduct, useProfitabilitySummary, useProfitabilitySummaryDaily } from '../../hooks';
import { PROFITABILITY_DATE_COLUMN, type ProfitabilitySummaryBreakdownQuery } from '../../models/Profitability';
import {
  ProfitabilityByProductCards,
  ProfitabilityKpiSection,
  ProfitabilitySetupAlerts,
  ProfitabilitySummaryDailyTable,
} from '../display';
import { ProfitabilityFiltersBar, type ProfitabilityFiltersState } from '../interaction/ProfitabilityFiltersBar';
import { ProfitabilitySummaryCogsModal } from '../interaction/ProfitabilitySummaryCogsModal';
import { ProfitabilitySummaryOrdersModal } from '../interaction/ProfitabilitySummaryOrdersModal';
import { ProfitabilitySummaryShippingCostsModal } from '../interaction/ProfitabilitySummaryShippingCostsModal';

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDefaultEndDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function isFormattedError(e: unknown): e is FormattedError {
  return typeof e === 'object' && e !== null && 'message' in e && typeof (e as FormattedError).message === 'string';
}

export const Main = () => {
  const { t, i18n } = useTranslation('profitabilityModule');
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    start_date: { type: 'string', default: getDefaultStartDate() },
    end_date: { type: 'string', default: getDefaultEndDate() },
    shop_id: { type: 'number', default: null as number | null },
  });

  const [filters, setFilters] = useState<ProfitabilityFiltersState>(() => ({
    startDate: queryParams.start_date,
    endDate: queryParams.end_date,
    shopId: queryParams.shop_id,
  }));

  useEffect(() => {
    setFilters({
      startDate: queryParams.start_date,
      endDate: queryParams.end_date,
      shopId: queryParams.shop_id,
    });
  }, [queryParams.start_date, queryParams.end_date, queryParams.shop_id]);

  const commonFilters: ProfitabilitySummaryBreakdownQuery | undefined = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return undefined;
    return {
      start_date: filters.startDate,
      end_date: filters.endDate,
      date_column: PROFITABILITY_DATE_COLUMN,
      shop_id: filters.shopId ?? undefined,
    };
  }, [filters]);

  const summaryQuery = useProfitabilitySummary(commonFilters, !!commonFilters);
  const dailyQuery = useProfitabilitySummaryDaily(commonFilters, !!commonFilters);
  const byProductQ = useProfitabilityByProduct(commonFilters, !!commonFilters);

  const isFetching = summaryQuery.isFetching || dailyQuery.isFetching || byProductQ.isFetching;

  const syncFiltersToUrl = useCallback(
    (next: ProfitabilityFiltersState) => {
      updateQueryParams({
        start_date: next.startDate,
        end_date: next.endDate,
        shop_id: next.shopId ?? undefined,
      });
    },
    [updateQueryParams],
  );

  const handleFiltersChange = (next: ProfitabilityFiltersState) => {
    setFilters(next);
    syncFiltersToUrl(next);
  };

  const firstError: unknown = summaryQuery.error || dailyQuery.error || byProductQ.error || null;

  const errorDescription = useMemo(() => {
    if (!firstError || !isFormattedError(firstError)) return null;
    if (firstError.statusCode === 403) {
      return firstError.message || t('errors.forbidden');
    }
    return firstError.message || t('errors.generic');
  }, [firstError, t]);

  const hasErrorNoData = !!errorDescription && summaryQuery.isError && !summaryQuery.data && !summaryQuery.isLoading;

  const [summaryOrdersOpen, setSummaryOrdersOpen] = useState(false);
  const [summaryCogsOpen, setSummaryCogsOpen] = useState(false);
  const [summaryShippingOpen, setSummaryShippingOpen] = useState(false);

  return (
    <div className="px-6 py-4 space-y-6" data-testid="profitability-main">
      <ProfitabilitySummaryOrdersModal
        open={summaryOrdersOpen}
        onOpenChange={setSummaryOrdersOpen}
        filters={commonFilters}
        locale={locale}
      />

      <ProfitabilitySummaryCogsModal
        open={summaryCogsOpen}
        onOpenChange={setSummaryCogsOpen}
        filters={commonFilters}
        locale={locale}
      />

      <ProfitabilitySummaryShippingCostsModal
        open={summaryShippingOpen}
        onOpenChange={setSummaryShippingOpen}
        filters={commonFilters}
        locale={locale}
      />

      <ProfitabilitySetupAlerts />

      <ProfitabilityFiltersBar
        value={filters}
        onChange={handleFiltersChange}
        getDefaultStartDate={getDefaultStartDate}
        getDefaultEndDate={getDefaultEndDate}
      />

      {hasErrorNoData ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title')}
          description={errorDescription ?? t('errors.generic')}
        />
      ) : (
        <div
          className={cn('space-y-6 transition-opacity duration-300', isFetching && 'opacity-50 pointer-events-none')}
        >
          <ProfitabilityKpiSection
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
            locale={locale}
            onOpenOrders={() => setSummaryOrdersOpen(true)}
            onOpenCogs={() => setSummaryCogsOpen(true)}
            onOpenShipping={() => setSummaryShippingOpen(true)}
          />

          <SectionBox data-testid="profitability-by-product-section">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-semibold text-title">{t('by_product.title')}</h2>
              <InfoTooltip
                text={t('by_product.hint')}
                dataTestId="profitability-by-product-info"
                iconClassName="w-4 h-4"
              />
            </div>

            <ProfitabilityByProductCards
              rows={byProductQ.data?.data ?? []}
              isLoading={byProductQ.isLoading}
              locale={locale}
            />
          </SectionBox>

          <SectionBox data-testid="profitability-summary-daily-section">
            <ProfitabilitySummaryDailyTable data={dailyQuery.data} isLoading={dailyQuery.isLoading} locale={locale} />
          </SectionBox>
        </div>
      )}
    </div>
  );
};
